import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import { EngagingIcon, ExpertIcon, UnionIcon } from '@segment/matcha/components/Icons/Brandbook'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

const featured = () =>
  useMemo(() => {
    const theme = useTheme()

    return [
      {
        key: `quick-and-easy-customization`,
        title: `Quick and easy customization`,
        description: `Customizing your Segment implementation requires just a few lines of JavaScript, freeing up engineering time for productive tasks that drive business value and less time spent wrestling with data pipeline workarounds. Functions is serverless and Segment does all the heavy lifting so you don't have to worry about provisioning additional server space to run them.`,
        icon: <EngagingIcon color={theme.palette.twilio.gray[100]} />,
      },
      {
        key: `developer-friendly`,
        title: `Developer-friendly`,
        description: `Functions is built by developers, for developers. Segment supports testing, version control, and code reusability features to help you collaborate and experiment freely - as well as Functions Co-Pilot serving as your trusty AI guide to orchestrate and optimize code in a snap.`,
        icon: <ExpertIcon color={theme.palette.twilio.gray[100]} />,
      },
      {
        key: `enterprise-grade-scalability`,
        title: `Enterprise-grade scalability`,
        description: `Not only do the biggest companies in the world trust Segment to process over a million API calls per second, but Functions also supports batching to help you stay within rate limits imposed by downstream providers.`,
        icon: <UnionIcon color={theme.palette.twilio.gray[100]} />,
      },
    ]
  }, [])

const FeatureBoxGroup = () => (
  <Wrapper>
    <SectionBaseParentWrapper variant="centered">
      <Section.Contents>
        <SectionBaseWrapper variant="centered">
          <StyledColumnTrack columns={1} overrides={{ lg: 3 }}>
            {featured().map((feature) => (
              <StyledFeatureBoxBase alignment="left" headerPosition="top" key={feature.key}>
                <FeatureBox.Header>
                  <IconWrapper background="white" elevation={1} variant="circle">
                    {feature.icon}
                  </IconWrapper>
                </FeatureBox.Header>
                <FeatureBox.Body>
                  <FeatureBox.Title variant="h4">{feature.title}</FeatureBox.Title>
                  <FeatureBox.Text>{feature.description}</FeatureBox.Text>
                </FeatureBox.Body>
              </StyledFeatureBoxBase>
            ))}
          </StyledColumnTrack>
        </SectionBaseWrapper>
      </Section.Contents>
    </SectionBaseParentWrapper>
  </Wrapper>
)

const StyledColumnTrack = styled(ColumnTrack)`
  max-width: 1180px;
`

const Wrapper = styled.div``

const SectionBaseParentWrapper = styled(SectionBase)`
  ${Section.Wrapper} {
    gap: 0;
  }
`

const SectionBaseWrapper = styled(SectionBase)`
  ${Section.Wrapper} {
    padding: 0;

    ${({ theme }) => theme.breakpoints.up(`sm`)} {
      max-width: 100%;
    }
  }
`

const StyledFeatureBoxBase = styled(FeatureBoxBase)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding: 20px 0px;
  }
`

export default FeatureBoxGroup
